import React, { useState, useEffect } from 'react';
import {
  getCommunityMembers,
  getPersonDetailsBatch,
  formatPersonName,
  getStatusConfig,
  getCommunityColor,
} from '../../../services/networkIntelligence.service';

/**
 * Modal showing all members of a specific community cluster
 * with their details and connection statistics.
 */
const CommunityMembersModal = ({
  isOpen,
  onClose,
  communityId,
  onPersonSelect,
}) => {
  const [communityStats, setCommunityStats] = useState(null);
  const [personDetails, setPersonDetails] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && communityId !== null && communityId !== undefined) {
      loadData();
    }
  }, [isOpen, communityId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const stats = await getCommunityMembers(communityId);

      if (stats) {
        setCommunityStats(stats);

        // Load person details for all members
        if (stats.members && stats.members.length > 0) {
          const personIds = stats.members.map(m => m.personId);
          const details = await getPersonDetailsBatch(personIds);
          setPersonDetails(details);
        }
      } else {
        setError('Could not load community data');
      }
    } catch (err) {
      console.error('Error loading community members:', err);
      setError('Failed to load community members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMemberClick = (member) => {
    if (onPersonSelect) {
      const details = personDetails.get(member.personId);
      onPersonSelect(member.personId, { ...member, details });
    }
  };

  if (!isOpen) return null;

  const communityColor = getCommunityColor(communityId);
  const members = communityStats?.members || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderBottomColor: communityColor, borderBottomWidth: '3px' }}
        >
          <div className="flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: communityColor }}
            >
              #{communityId}
            </span>
            <div>
              <h2 className="text-xl font-bold" style={{ color: communityColor }}>
                Community #{communityId}
              </h2>
              <p className="text-sm text-gray-500">
                People who are frequently co-located together
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div
                className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full"
                style={{ borderColor: communityColor, borderTopColor: 'transparent' }}
              ></div>
              <span className="ml-3 text-gray-500">Loading community members...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Community Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div
                  className="rounded-lg p-4"
                  style={{ backgroundColor: `${communityColor}10` }}
                >
                  <div className="text-sm" style={{ color: communityColor }}>Members</div>
                  <div className="text-2xl font-bold" style={{ color: communityColor }}>
                    {communityStats?.memberCount || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-600 text-sm">Total Connections</div>
                  <div className="text-2xl font-bold">
                    {communityStats?.totalConnections || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-600 text-sm">Avg Connections</div>
                  <div className="text-2xl font-bold">
                    {communityStats?.avgConnections?.toFixed(1) || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-600 text-sm">Central Members</div>
                  <div className="text-2xl font-bold">
                    {communityStats?.centralMembers || 0}
                  </div>
                </div>
              </div>

              {/* What is a Community */}
              <div
                className="rounded-lg p-4 text-sm"
                style={{ backgroundColor: `${communityColor}10`, borderLeft: `4px solid ${communityColor}` }}
              >
                <div className="font-medium mb-1" style={{ color: communityColor }}>
                  What does this mean?
                </div>
                <p className="text-gray-700">
                  A <strong>community</strong> is a group of people who are more connected to each other
                  than to people outside the group. Members of this community have been frequently
                  co-located (same place, same time) with each other, suggesting they may travel
                  together or share common locations.
                </p>
              </div>

              {/* Members Table */}
              <div className="bg-white rounded-lg border shadow-sm">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">
                    Community Members
                  </h3>
                  <span className="text-xs text-gray-500">
                    Click a row to investigate
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {members.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No members found
                    </div>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">ID</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-center">Connections</th>
                          <th className="px-4 py-2 text-center">Role</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {members.map((member, idx) => {
                          const details = personDetails.get(member.personId);
                          const statusConfig = getStatusConfig(details?.status || details?.Status);

                          return (
                            <tr
                              key={member.personId}
                              className="hover:bg-blue-50 cursor-pointer transition-colors"
                              onClick={() => handleMemberClick(member)}
                            >
                              <td className="px-4 py-2">
                                <div className="font-medium text-gray-900">
                                  {details ? formatPersonName(details) : (
                                    <span className="text-gray-400 italic">Loading...</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-gray-600">
                                {member.personId}
                              </td>
                              <td className="px-4 py-2">
                                {details ? (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                    style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                                  >
                                    {statusConfig.label}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className="font-medium">{member.connections}</span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                {member.isCentral ? (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                    style={{ backgroundColor: communityColor }}
                                  >
                                    Central
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">Member</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Central Members Explanation */}
              {communityStats?.centralMembers > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                  <div className="font-medium text-amber-800 mb-1">
                    Central Members
                  </div>
                  <p className="text-amber-700">
                    <strong>{communityStats.centralMembers}</strong> member{communityStats.centralMembers > 1 ? 's' : ''} in this
                    community {communityStats.centralMembers > 1 ? 'have' : 'has'} 3 or more connections, making them
                    potential "hubs" of the group. Central members may be coordinators or key figures
                    within the community.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityMembersModal;
